import { NavigateFunction, NavigateOptions } from 'react-router-dom';

type SafeNavigateOptions = {
	search?: string;
	state?: any;
	replace?: boolean;
};

/**
 * Safely navigate to a path after validating it to prevent open redirects.
 * Only navigates if the path is a safe relative path (starts with / but not //).
 *
 * @param navigate - React Router's navigate function
 * @param path - The path to navigate to
 * @param search - Optional search params string (should start with ?) - for backward compatibility
 * @param options - Optional navigation options (search, state, replace, etc.)
 * @returns true if navigation occurred, false if path was invalid
 */
export const safeNavigate = (
	navigate: NavigateFunction,
	path: string,
	search?: string | SafeNavigateOptions,
	options?: NavigateOptions,
): boolean => {
	// Validate path is a safe relative path (starts with / but not //)
	if (!path || !path.startsWith('/') || path.startsWith('//')) {
		return false;
	}

	// Handle backward compatibility: search can be a string or options object
	let searchParam: string | undefined;
	let navOptions: NavigateOptions | undefined = options;

	if (typeof search === 'string') {
		searchParam = search;
	} else if (search && typeof search === 'object') {
		searchParam = search.search;
		navOptions = {
			...navOptions,
			...(search.state && { state: search.state }),
			...(search.replace !== undefined && { replace: search.replace }),
		};
	}

	if (searchParam || navOptions) {
		navigate(
			{
				pathname: path,
				...(searchParam && { search: searchParam }),
			},
			navOptions,
		);
	} else {
		navigate(path);
	}

	return true;
};
